const SELECTORS = {
  listItem: "Accordion-entry",
  headline: "Accordion-entryHeadline",
  button: "Accordion-entryButton",
  template: "Accordion-entryButtonTemplate",
  isClosed: "is-closed",
};

class Accordion {
  /**
   * @param {HTMLElement} accordion
   */
  constructor(accordion) {
    this.accordion = accordion;
    this.headlineLevel = this.accordion.dataset.headlineLevel;
    this._queryElements();
    this._changeHeadlinesLevelIfNecessary();
    this._prepareAccordion();
    this._addEventListener();
  }

  /**
   * Query all necessary elements
   */
  _queryElements() {
    this.items = this.accordion.querySelectorAll(`.${SELECTORS.listItem}`);
  }

  /**
   * Headlines are in sequentially order for rendering wo. js.
   * If accordion is included in another template it could be necessary
   * to change the level
   * For example: patterns/faq
   *   h2 - template headline
   *   h3 - accordion item headline
   */
  _changeHeadlinesLevelIfNecessary() {
    if (this.headlineLevel !== undefined) {
      this.accordion
        .querySelectorAll(`.${SELECTORS.headline}`)
        .forEach((headline) => {
          if (headline.tagName !== `H${this.headlineLevel}`) {
            const h3 = document.createElement(`H${this.headlineLevel}`);
            h3.innerHTML = headline.innerHTML;
            [...headline.attributes].forEach((attr) => {
              h3.setAttribute(attr.nodeName, attr.nodeValue);
            });
            headline.parentNode.replaceChild(h3, headline);
          }
        });
    }
  }

  /**
   * Query all items and call the replace headline<->template
   * method
   */
  _prepareAccordion() {
    this.items.forEach((item) => {
      Accordion.replaceHeadlineWithTemplate(item);
      const button = item.querySelector(`.${SELECTORS.button}`);
      const buttonControls = this.accordion.querySelector(
        `#${button.getAttribute("aria-controls")}`
      );
      if (button.dataset.isOpenOnLoad === "true") {
        Accordion.openListItem(buttonControls, button);
      } else {
        Accordion.closeListItem(buttonControls, button);
      }
    });
  }

  /**
   * Add event listener for a button click
   */
  _addEventListener() {
    this.items.forEach((item) => {
      item
        .querySelector(`.${SELECTORS.button}`)
        .addEventListener("click", (event) => {
          this._handleClickEvent(event.currentTarget);
        });
    });
  }

  /**
   * Iterate over the accordion, close all items except the one
   * thats the event target. If that is open already, clsoe it.
   *
   * @param {HTMLElement} targetButton
   */
  _handleClickEvent(targetButton) {
    const isExpanded = targetButton.getAttribute("aria-expanded");
    const buttonControls = this.accordion.querySelector(
      `#${targetButton.getAttribute("aria-controls")}`
    );
    this.items.forEach((item) => {
      if (item.contains(buttonControls) && isExpanded === "false") {
        Accordion.openListItem(buttonControls, targetButton);
      } else {
        const currentButton = item.querySelector(`.${SELECTORS.button}`);
        const currentButtonControls = this.accordion.querySelector(
          `#${currentButton.getAttribute("aria-controls")}`
        );
        Accordion.closeListItem(currentButtonControls, currentButton);
      }
    });
  }

  /**
   * Set aria-expanded to true and remove
   * the is-closed class
   *
   * @param {HTMLElement} buttonControls
   * @param {HTMLElement} button
   */
  static openListItem(buttonControls, button) {
    const listItemToOpen = buttonControls.closest(`.${SELECTORS.listItem}`);
    listItemToOpen.classList.remove(SELECTORS.isClosed);
    button.setAttribute("aria-expanded", "true");
  }

  /**
   * Set aria-expanded to false and add
   * the is-closed class
   *
   * @param {HTMLElement} buttonControls
   * @param {HTMLElement} button
   */
  static closeListItem(buttonControls, button) {
    const listItemToClose = buttonControls.closest(`.${SELECTORS.listItem}`);
    listItemToClose.classList.add(SELECTORS.isClosed);
    button.setAttribute("aria-expanded", "false");
  }

  /**
   * Replace the headline with the button template
   *
   * @param {HTMLElement} item
   */
  static replaceHeadlineWithTemplate(item) {
    item
      .querySelector(`.${SELECTORS.headline}`)
      .replaceChildren(
        item.querySelector(`.${SELECTORS.template}`).content.cloneNode(true)
      );
  }
}

export default Accordion;
